import {
  AppointmentUpdateData,
  StepUpdateData,
  TreatmentPlanAppointment,
  CodeSuggestion,
} from "../services/treatmentPlanService";
import {
  EditableAppointmentData,
  EditableProcedureData,
} from "../types/editableTreatmentPlan";
import { sanitizeFeeInput } from "./treatmentPlanDraftUtils";
import {
  buildToothSurfaces,
  filterSurfacesForCapabilities,
  hasPureAreaSelection,
  keepOnlyAreaSurfaces,
  parseToothSurfaces,
} from "./toothSurfacesUtils";
import {
  aggregateTreatAreaCapabilities,
  canSelectSurfaces,
  canSelectTeeth,
  EMPTY_TREAT_AREA_CAPABILITIES,
  normalizeTreatAreaCapabilities,
  TreatAreaCapabilities,
} from "./treatAreaCapabilities";

export function buildCodeTreatAreaFlagsByCode(
  sources: Array<{
    code?: string | null;
    needsTooth?: boolean | null;
    needsSurface?: boolean | null;
    needsRange?: boolean | null;
    treatArea?: number | string | null;
  }>,
): Record<string, TreatAreaCapabilities> {
  const result: Record<string, TreatAreaCapabilities> = {};
  sources.forEach((source) => {
    if (!source.code) return;
    result[source.code] = normalizeTreatAreaCapabilities(source);
  });
  return result;
}

export function resolveTreatAreaCapabilitiesForCodes(
  codes: string[],
  flagsByCode: Record<string, TreatAreaCapabilities>,
  catalogLookup?: Array<{
    code: string;
    needsTooth?: boolean | null;
    needsSurface?: boolean | null;
    needsRange?: boolean | null;
    treatArea?: number | string | null;
  }>,
): {
  flagsByCode: Record<string, TreatAreaCapabilities>;
  capabilities: TreatAreaCapabilities;
} {
  const nextFlags: Record<string, TreatAreaCapabilities> = {};
  codes.forEach((code) => {
    const fromCatalog = catalogLookup?.find((item) => item.code === code);
    const fromFlags = flagsByCode[code];

    // Prefer CDT catalog when present — plan codeSuggestions often omit treatArea,
    // which would otherwise leave Quad/Sextant codes on legacy "all tabs" flags.
    if (fromCatalog) {
      nextFlags[code] = normalizeTreatAreaCapabilities(fromCatalog);
      return;
    }
    if (fromFlags) {
      nextFlags[code] = fromFlags;
      return;
    }
    nextFlags[code] = normalizeTreatAreaCapabilities(null);
  });

  return {
    flagsByCode: nextFlags,
    capabilities: codes.length
      ? aggregateTreatAreaCapabilities(Object.values(nextFlags))
      : { ...EMPTY_TREAT_AREA_CAPABILITIES },
  };
}

/**
 * A step linked to a Custom Procedure is a bundle: it is priced by the procedure
 * fee and its member codes are not standalone rows (OP-665).
 */
export function isBundleStep(customProcedureId?: number | null): boolean {
  return customProcedureId != null;
}

/**
 * Bundle member codes arrive with a null id — they render the bundle contents and
 * must never be echoed back as standalone codes.
 */
export function isPersistedCodeSuggestion(suggestion: {
  id?: number | null;
}): boolean {
  return suggestion.id != null && suggestion.id > 0;
}

/**
 * codeSuggestions to send in PUT: a bundle step persists only the codes added
 * outside the bundle, a plain step persists all of them.
 */
export function codeSuggestionsForPayload<T extends { id?: number | null }>(
  customProcedureId: number | null | undefined,
  codeSuggestions: T[],
): T[] {
  return isBundleStep(customProcedureId)
    ? codeSuggestions.filter(isPersistedCodeSuggestion)
    : codeSuggestions;
}

/**
 * Codes an editable step persists. `cdtCodes` holds only the codes picked outside
 * a bundle, so a bundle step contributes just those; a plain step contributes all
 * of its resolved codes.
 */
export function persistableCodesForProcedure(procedure: {
  customProcedureId?: number;
  cdtCodes: string[];
  resolvedCdtCodes: string[];
}): string[] {
  return isBundleStep(procedure.customProcedureId)
    ? procedure.cdtCodes
    : procedure.resolvedCdtCodes;
}

/**
 * Codes contributed by the bundle itself. Their composition is owned by the Custom
 * Procedure settings, so they must not be individually editable inside a plan.
 */
export function bundleMemberCodes(procedure: {
  customProcedureId?: number;
  cdtCodes: string[];
  resolvedCdtCodes: string[];
}): string[] {
  if (!isBundleStep(procedure.customProcedureId)) return [];
  const standalone = new Set(procedure.cdtCodes);
  return procedure.resolvedCdtCodes.filter((code) => !standalone.has(code));
}

/** Converts appointments to the PUT body format */
export function buildAppointmentUpdateData(
  appointments: TreatmentPlanAppointment[],
): AppointmentUpdateData[] {
  return [...appointments]
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((appt) => ({
      id: appt.id,
      timeframeLabel: appt.timeframeLabel,
      conditionalPath: appt.conditionalPath || undefined,
      description: appt.description || undefined,
      sortOrder: appt.sortOrder,
      totalDurationMinutes: appt.totalDurationMinutes,
      steps: [...appt.steps]
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map(
          (step): StepUpdateData => ({
            id: step.id,
            name: step.name,
            description: step.description || undefined,
            sortOrder: step.sortOrder,
            durationMinutes: step.durationMinutes,
            fee: step.fee,
            // Sent explicitly — omitting it unlinks the step from its procedure.
            customProcedureId: step.customProcedureId ?? null,
            isFeeEdited: step.isFeeEdited,
            toothSurfaces: step.toothSurfaces ?? [],
            codeSuggestions: codeSuggestionsForPayload(
              step.customProcedureId,
              step.codeSuggestions,
            ).map((cs) => ({
              id: cs.id,
              cdtCodeId: cs.cdtCodeId,
            })),
          }),
        ),
    }));
}

/** Converts one editable step into its PUT payload. */
export function buildStepUpdateData(
  procedure: EditableProcedureData,
  cdtCodes: Array<{ id: number; code: string }>,
): StepUpdateData {
  const stepData: StepUpdateData = {
    name: procedure.text,
    description: procedure.description || undefined,
    sortOrder: procedure.sortOrder,
    durationMinutes: procedure.durationMinutes ?? undefined,
    fee: procedure.fee !== "" ? parseFloat(procedure.fee) : undefined,
    // Sent explicitly — omitting it unlinks the step from its procedure.
    customProcedureId: procedure.customProcedureId ?? null,
    isFeeEdited: procedure.isFeeEdited,
    toothSurfaces: buildToothSurfaces(
      procedure.teeth,
      procedure.surfaces,
      procedure.treatAreaCapabilities,
    ),
    codeSuggestions: persistableCodesForProcedure(procedure)
      .map((code) => {
        const found = cdtCodes.find((c) => c.code === code);
        return found ? { cdtCodeId: found.id } : null;
      })
      .filter((s): s is { cdtCodeId: number } => s !== null),
  };

  // Only include id for existing steps (id must be >= 1)
  if (procedure.id && procedure.id >= 1) {
    stepData.id = procedure.id;
  }

  return stepData;
}

export function convertToEditableAppointment(
  apiAppointment: TreatmentPlanAppointment,
): EditableAppointmentData {
  const computedDuration = apiAppointment.steps.reduce(
    (sum, step) => sum + (step.durationMinutes ?? 20),
    0,
  );

  return {
    id: apiAppointment.id,
    timeframeValue: apiAppointment.timeframeLabel || "",
    timeframeUnit: "",
    conditionalPath: apiAppointment.conditionalPath || "",
    description: apiAppointment.description || "",
    sortOrder: apiAppointment.sortOrder,
    totalDurationMinutes:
      apiAppointment.totalDurationMinutes ?? computedDuration,
    isDurationEdited:
      apiAppointment.totalDurationMinutes != null &&
      apiAppointment.totalDurationMinutes !== computedDuration,
    procedures: apiAppointment.steps.map((step) => {
      const customProcedureId = step.customProcedureId ?? undefined;
      const explanation = step.codeSuggestions
        .map((cs) => cs.description)
        .filter(Boolean)
        .join("\n");
      // A bundle costs the procedure fee — never the sum of its member codes.
      const feeFallback = isBundleStep(customProcedureId)
        ? 0
        : step.codeSuggestions.reduce((sum, cs) => sum + (cs.defaultFee || 0), 0);
      const codeSuggestionCodes = step.codeSuggestions.map((cs) => cs.code);
      // Codes added outside the bundle keep a real id; bundle members come as null.
      const standaloneCodes = codeSuggestionsForPayload(
        customProcedureId,
        step.codeSuggestions,
      ).map((cs) => cs.code);
      const parsed = parseToothSurfaces(step.toothSurfaces);
      const codeTreatAreaFlagsByCode = buildCodeTreatAreaFlagsByCode(
        step.codeSuggestions as CodeSuggestion[],
      );
      const treatAreaCapabilities = aggregateTreatAreaCapabilities(
        Object.values(codeTreatAreaFlagsByCode),
      );

      let teeth = parsed.teeth;
      let surfaces = parsed.surfaces;
      if (!canSelectTeeth(treatAreaCapabilities)) {
        teeth = [];
      }
      if (!canSelectSurfaces(treatAreaCapabilities)) {
        surfaces = [];
      } else {
        surfaces = filterSurfacesForCapabilities(
          surfaces,
          treatAreaCapabilities,
        );
      }
      // Q/S/A treat areas must not keep a stale tooth range from the API.
      if (hasPureAreaSelection(surfaces)) {
        teeth = [];
        surfaces = keepOnlyAreaSurfaces(surfaces);
      }

      return {
        id: step.id,
        text: step.name,
        description: step.description || "",
        sortOrder: step.sortOrder,
        cdtCodes: standaloneCodes,
        customProcedureId,
        resolvedCdtCodes: codeSuggestionCodes,
        explanation,
        teeth,
        surfaces,
        durationMinutes: step.durationMinutes ?? 20,
        fee: sanitizeFeeInput(String(step.fee ?? feeFallback)),
        isFeeEdited: step.isFeeEdited ?? false,
        codeTreatAreaFlagsByCode,
        treatAreaCapabilities,
      };
    }),
  };
}

/** Map editable procedure teeth/surfaces into API toothSurfaces for local state. */
export function toothSurfacesFromProcedure(procedure: {
  teeth: string[];
  surfaces: string[];
  treatAreaCapabilities?: TreatAreaCapabilities;
}) {
  return buildToothSurfaces(
    procedure.teeth,
    procedure.surfaces,
    procedure.treatAreaCapabilities,
  );
}
